import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAuthProviderUrl } from '../config/runtime';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { isNativeApp } from '../utils/appBridge';
import { Browser } from '@capacitor/browser';

/**
 * 소셜 로그인 시작
 * - 웹: 현재 창에서 OAuth 진행
 * - 앱: 구글이 웹뷰 OAuth를 차단하므로 외부 브라우저(Chrome Custom Tabs)로 열고
 *        완료 후 딥링크(freematch://auth)로 복귀 (appNative.ts에서 처리)
 */
const startSocialLogin = (provider: string) => {
  console.log(`${provider} 소셜 로그인 시도 (native: ${isNativeApp()})`);
  const url = getAuthProviderUrl(provider);
  if (isNativeApp()) {
    const sep = url.includes('?') ? '&' : '?';
    Browser.open({ url: `${url}${sep}app=1` });
  } else {
    window.location.href = url;
  }
};

const S = {
  page: {
    minHeight: '100vh', background: '#000',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Pretendard', -apple-system, sans-serif",
    position: 'relative' as const,
    overflow: 'hidden',
  } as React.CSSProperties,
  box: {
    width: '100%', maxWidth: 400,
    background: '#0d0d0d', borderRadius: 16,
    padding: '40px 36px',
    border: '1px solid rgba(255,255,255,0.1)',
    position: 'relative' as const,
    zIndex: 1,
  } as React.CSSProperties,
  input: {
    width: '100%', padding: '13px 14px 13px 42px',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9,
    fontSize: 15, outline: 'none', boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s',
    background: 'rgba(255,255,255,0.05)',
    color: '#fff',
  } as React.CSSProperties,
  inputWrap: { position: 'relative' as const, marginBottom: 12 },
  icon: {
    position: 'absolute' as const, left: 13, top: '50%',
    transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)', pointerEvents: 'none' as const,
  },
  divider: { display: 'flex', alignItems: 'center', margin: '20px 0' } as React.CSSProperties,
  line: { flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' } as React.CSSProperties,
};

/* 카카오 SVG 로고 */
const KakaoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#191919">
    <path d="M12 3C6.477 3 2 6.477 2 10.8c0 2.715 1.633 5.1 4.1 6.525L5.1 21l4.55-2.725A11.8 11.8 0 0 0 12 18.6c5.523 0 10-3.477 10-7.8S17.523 3 12 3z"/>
  </svg>
);

/* 구글 SVG 로고 */
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

/* 애플 SVG 로고 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const AppleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="white">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, user, token } = useAuth();

  useEffect(() => {
    if (user && token) navigate('/services');
  }, [user, token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('로그인 시도:', email);
    setError(''); setLoading(true);
    try {
      await login(email, password);
      console.log('로그인 성공');
      navigate('/services');
    } catch (err: any) {
      console.error('로그인 에러:', err);
      setError(err.response?.data?.message || '이메일 또는 비밀번호를 확인해주세요.');
    } finally { setLoading(false); }
  };

  const socialBtn = (bg: string, color: string, icon: React.ReactNode, label: string, provider: string) => (
    <button
      onClick={() => startSocialLogin(provider)}
      style={{
        width: '100%', padding: '12px', background: bg, color,
        border: provider === 'apple' ? 'none' : `1.5px solid ${bg === '#fff' ? '#E8E8E8' : 'transparent'}`,
        borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        marginBottom: 8, transition: 'opacity 0.15s',
      }}
      onMouseOver={e => e.currentTarget.style.opacity = '0.88'}
      onMouseOut={e => e.currentTarget.style.opacity = '1'}
    >
      {icon}{label}
    </button>
  );

  return (
    <div style={S.page}>
      <div style={S.box}>
        {/* 로고 */}
        {/* 글로우 배경 */}
        <div style={{
          position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)',
          width: 300, height: 200,
          background: 'radial-gradient(ellipse, rgba(124,58,237,0.25) 0%, transparent 70%)',
          pointerEvents: 'none', zIndex: 0,
        }} />
        <div style={{ textAlign: 'center', marginBottom: 32, position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', marginBottom: 6 }}>
            freematch
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>{t('login.subtitle')}</div>
        </div>

        {/* 소셜 로그인 */}
        {socialBtn('#FEE500', '#191919', <KakaoIcon />, t('login.kakao'), 'kakao')}
        {socialBtn('#fff', '#333', <GoogleIcon />, t('login.google'), 'google')}
        {/* Apple 로그인: 백엔드 미구현 + Apple Developer($99/년) 필요 — iOS 출시 시 활성화
        {socialBtn('#000', '#fff', <AppleIcon />, 'Apple로 로그인', 'apple')} */}

        {/* 구분선 */}
        <div style={S.divider}>
          <div style={S.line} />
          <span style={{ margin: '0 14px', fontSize: 12, color: 'rgba(255,255,255,0.2)' }}>{t('login.or')}</span>
          <div style={S.line} />
        </div>

        {/* 이메일 폼 */}
        <form onSubmit={handleSubmit}>
          <div style={S.inputWrap}>
            <span style={S.icon}><Mail size={16} /></span>
            <input
              type="email" placeholder={t('login.email')} value={email}
              onChange={e => setEmail(e.target.value)} required
              style={S.input}
              onFocus={e => e.target.style.borderColor = '#4F46E5'}
              onBlur={e => e.target.style.borderColor = '#E8E8E8'}
            />
          </div>
          <div style={S.inputWrap}>
            <span style={S.icon}><Lock size={16} /></span>
            <input
              type="password" placeholder={t('login.password')} value={password}
              onChange={e => setPassword(e.target.value)} required
              style={S.input}
              onFocus={e => e.target.style.borderColor = '#4F46E5'}
              onBlur={e => e.target.style.borderColor = '#E8E8E8'}
            />
          </div>

          {error && (
            <div style={{ fontSize: 13, color: '#fca5a5', marginBottom: 12, padding: '10px 12px', background: 'rgba(239,68,68,0.12)', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)' }}>
              {error}
            </div>
          )}

          <button
            type="submit" disabled={loading}
            style={{
              width: '100%', padding: '13px',
              background: loading ? 'rgba(255,255,255,0.4)' : '#fff', color: '#000',
              border: 'none', borderRadius: 9, fontSize: 15, fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'opacity 0.15s',
            }}
          >
            {loading ? '로그인 중...' : <><span>{t('login.emailLogin')}</span><ArrowRight size={16} /></>}
          </button>
        </form>

        {/* 하단 링크 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20, fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Link to="/find-userid" style={{ color: 'rgba(255,255,255,0.35)', textDecoration: 'none' }}>{t('login.findId')}</Link>
            <span>|</span>
            <Link to="/forgot-password" style={{ color: 'rgba(255,255,255,0.35)', textDecoration: 'none' }}>{t('login.findPassword')}</Link>
          </div>
          <Link to="/register" style={{ color: '#a78bfa', fontWeight: 600, textDecoration: 'none' }}>
            {t('login.register')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
